// 3-5-8 (the Polish rules, owner 2026-10-28, .ai/358.md): 3 players, 52 cards, 16 each and 4 in the musik. The player
// left of the dealer declares: a trump suit, "bez atu" (no trumps) or "bez lew" (take as few tricks as you can), seeing
// only his first 6 cards; every seat declares each of the 6 games at most once. Then the card exchange (in a trump game
// only): a seat in plus after the last deal gives as many cards as its plus to a seat in minus, which gives back its
// highest card of that suit (the same card when it has none of it; for a trump any card it likes). Then the declarer
// takes the musik (shown to all) and lays 4 cards away. The declarer leads; follow suit; without the suit a trump must
// be played when there is one (no duty to overtrump); else any card. The targets: the declarer 8, the dealer 3, the
// third seat 5, points = tricks - target; "bez lew": the declarer 3, the dealer 8, the third 5, points = target -
// tricks. The points of a deal add up to 0. The game lasts the deals the player chose; the highest total wins.
//
// Pure (no DOM, no React, no Math.random), as hearts/rules.ts. The cards and their helpers are Kierki's (the REAL
// suits; `fileId` maps them to the picture files). Seats: 0 the player at the bottom, 1 left, 2 right; play goes
// clockwise (0, 1, 2) and the deal moves left (the next dealer is the seat after the dealer).
import { shuffle } from '../engine/shuffle';
import { BASE_DECK, cardKey, sameCard, type HCard, type HSuit, type Played } from '../hearts/rules';

export { BASE_DECK, cardKey, fileId, sameCard, type HCard, type HSuit, type Played } from '../hearts/rules';

export const SEATS = 3;
export const HAND = 16;
/** A trump suit, NT "bez atu" (no trumps), NL "bez lew" (fewest tricks). */
export type Contract = HSuit | 'NT' | 'NL';
/** The order the declarer's choice shows them in. */
export const CONTRACTS: readonly Contract[] = ['S', 'H', 'D', 'C', 'NT', 'NL'];
/** The game lengths offered before the game (owner 2026-10-28): 3 = each seat declares once, 18 = the full game (each
 *  seat each of the 6 games). */
export const LENGTHS = [3, 6, 9, 18] as const;
export const DEFAULT_LENGTH = 6;

export type Phase = 'declare' | 'exchange' | 'musik' | 'play' | 'trick-done' | 'hand-end' | 'over';

/** Cards still to change hands between a seat in plus (`giver`) and one in minus (`taker`). */
export interface SwapRight {
  readonly giver: number;
  readonly taker: number;
  readonly left: number;
}
/** One exchange done: `gave` went from the giver to the taker, `got` came back (the same card: nothing changed). */
export interface SwapDone {
  readonly giver: number;
  readonly taker: number;
  readonly gave: HCard;
  readonly got: HCard;
}
export interface DealResult {
  readonly contract: Contract;
  readonly declarer: number;
  readonly dealer: number;
  readonly tricks: readonly number[];
  readonly targets: readonly number[];
  readonly points: readonly number[];
}

export interface State358 {
  /** The deals of this game (LENGTHS). */
  readonly length: number;
  /** Deals played before this one (0 = the first). */
  readonly handNo: number;
  readonly dealer: number;
  readonly declarer: number;
  readonly hands: readonly (readonly HCard[])[];
  /** The declarer's first 6 cards: all he sees while he declares. */
  readonly firstSix: readonly HCard[];
  /** The 4 cards of the musik until the declarer takes them. */
  readonly musik: readonly HCard[];
  /** The musik's cards, also after they were taken (the view shows them in the hand for a moment). */
  readonly musikCards: readonly HCard[];
  /** The 4 cards the declarer laid away (out of this deal). */
  readonly discards: readonly HCard[];
  readonly contract: Contract | null;
  readonly phase: Phase;
  /** The exchange still to come, the first pair first. */
  readonly swaps: readonly SwapRight[];
  readonly swapLog: readonly SwapDone[];
  /** A trump was given: the taker gives back a card of its choice. */
  readonly pendingTrump: { readonly giver: number; readonly taker: number; readonly card: HCard } | null;
  readonly trick: readonly Played[];
  /** Whose card is next (-1 outside the play or with the trick full). */
  readonly turn: number;
  /** Who took the full trick (phase trick-done), else -1. */
  readonly winner: number;
  /** Tricks taken in this deal. */
  readonly tricks: readonly number[];
  /** The cards played in the finished tricks of this deal (the computer players remember them, as a player would). */
  readonly gone: readonly HCard[];
  /** Totals of the finished deals. */
  readonly scores: readonly number[];
  readonly history: readonly DealResult[];
  /** The games each seat has declared. */
  readonly used: readonly (readonly Contract[])[];
  /** The shuffle pattern of this deal (52 numbers, engine/shuffle.ts). */
  readonly pattern: readonly number[];
}

const has = (cards: readonly HCard[], c: HCard) => cards.some((x) => sameCard(x, c));
const without = (cards: readonly HCard[], out: readonly HCard[]) => cards.filter((c) => !has(out, c));
const HAND_ORDER: readonly HSuit[] = ['C', 'D', 'S', 'H'];
/** A hand in the order players hold it: clubs, diamonds, spades, hearts (colours alternate), low to high; the trump
 *  suit at the right end (where a player keeps it). */
export function sortHand(h: readonly HCard[], trump: HSuit | null = null): HCard[] {
  const order = trump ? [...HAND_ORDER.filter((s) => s !== trump), trump] : HAND_ORDER;
  return h.slice().sort((a, b) => order.indexOf(a.s) - order.indexOf(b.s) || a.r - b.r);
}
export const trumpOf = (c: Contract | null): HSuit | null => (c === null || c === 'NT' || c === 'NL' ? null : c);
export const isLowGame = (c: Contract | null): boolean => c === 'NL';
/** What a seat is in this deal: 0 the dealer, 1 the declarer, 2 the third. */
export const roleOf = (s: Pick<State358, 'dealer'>, seat: number): number => (seat - s.dealer + SEATS) % SEATS;
/** The tricks a seat must take (or, in "bez lew", may take at most). */
export function targetOf(s: Pick<State358, 'dealer' | 'contract'>, seat: number): number {
  const role = roleOf(s, seat);
  if (isLowGame(s.contract)) return [8, 3, 5][role]!;
  return [3, 8, 5][role]!;
}
export const targets = (s: Pick<State358, 'dealer' | 'contract'>): number[] => [0, 1, 2].map((p) => targetOf(s, p));
/** The points of a deal from the tricks taken. */
export function dealPoints(s: Pick<State358, 'dealer' | 'contract'>, tricks: readonly number[]): number[] {
  return tricks.map((w, p) => (isLowGame(s.contract) ? targetOf(s, p) - w : w - targetOf(s, p)));
}
/** The games `seat` may still declare (all again if it has used all six). */
export function available(s: Pick<State358, 'used'>, seat: number): Contract[] {
  const left = CONTRACTS.filter((c) => !s.used[seat]!.includes(c));
  return left.length ? left : CONTRACTS.slice();
}

/** A new game of `length` deals: the player declares first (the dealer is seat 2). */
export function startGame(length: number, pattern: readonly number[] | null, rng: () => number): State358 {
  return startHand({ length, handNo: -1, dealer: 1, scores: [0, 0, 0], history: [], used: [[], [], []] }, pattern, rng);
}

/** The next deal of a game (handNo + 1, the deal one seat on); `pattern` replays one (Retry, a report). */
export function startHand(prev: Pick<State358, 'length' | 'handNo' | 'dealer' | 'scores' | 'history' | 'used'>, pattern: readonly number[] | null, rng: () => number): State358 {
  const { order, pattern: used } = shuffle(BASE_DECK.length, pattern, rng);
  const cards = order.map((i) => BASE_DECK[i]!);
  const dealer = (prev.dealer + 1) % SEATS;
  const declarer = (dealer + 1) % SEATS;
  // dealt in order from the declarer round the table; the 4 last cards of the shuffle are the musik
  const dealt = [0, 1, 2].map((k) => cards.slice(k * HAND, k * HAND + HAND));
  const hands: HCard[][] = [[], [], []];
  for (let k = 0; k < SEATS; k++) hands[(declarer + k) % SEATS] = dealt[k]!;
  return {
    length: prev.length, handNo: prev.handNo + 1, dealer, declarer, hands: hands.map((h) => sortHand(h)),
    firstSix: sortHand(hands[declarer]!.slice(0, 6)), musik: cards.slice(48), musikCards: cards.slice(48), discards: [], contract: null,
    phase: 'declare', swaps: [], swapLog: [], pendingTrump: null, trick: [], turn: -1, winner: -1, tricks: [0, 0, 0], gone: [],
    scores: prev.scores, history: prev.history, used: prev.used, pattern: used,
  };
}

/** Who gives how many cards to whom: each seat in plus after the last deal (from the declarer round the table) to the
 *  seats in minus, as many as its plus, each seat in minus taking as many as its minus. Only in a trump game. */
export function exchangeRights(s: Pick<State358, 'history' | 'declarer' | 'contract'>): SwapRight[] {
  const last = s.history[s.history.length - 1];
  if (!last || trumpOf(s.contract) === null) return [];
  const short = last.points.map((p) => Math.max(0, -p));
  const out: SwapRight[] = [];
  for (let k = 0; k < SEATS; k++) {
    const giver = (s.declarer + k) % SEATS;
    let n = Math.max(0, last.points[giver]!);
    for (let j = 1; j < SEATS && n > 0; j++) {
      const taker = (giver + j) % SEATS;
      const m = Math.min(n, short[taker]!);
      if (m <= 0) continue;
      out.push({ giver, taker, left: m });
      n -= m;
      short[taker]! -= m;
    }
  }
  return out;
}

/** The declarer names the game; then the exchange (if any) or the musik. Null: not allowed. */
export function declare(s: State358, seat: number, c: Contract): State358 | null {
  if (s.phase !== 'declare' || seat !== s.declarer || !available(s, seat).includes(c)) return null;
  const used = s.used.map((u, i) => (i === seat ? [...u, c] : u));
  const trump = trumpOf(c);
  const next: State358 = { ...s, contract: c, used, hands: s.hands.map((h) => sortHand(h, trump)) };
  const swaps = exchangeRights(next);
  return swaps.length ? { ...next, phase: 'exchange', swaps } : { ...next, phase: 'musik' };
}

const highestOf = (cards: readonly HCard[]) => cards.reduce((m, c) => (c.r > m.r ? c : m));
const lowestOf = (cards: readonly HCard[]) => cards.reduce((m, c) => (c.r < m.r ? c : m));

/** Gives `card` from `from` to `to` and `back` the other way. */
function trade(s: State358, from: number, to: number, card: HCard, back: HCard): State358 {
  const trump = trumpOf(s.contract);
  const hands = s.hands.map((h, i) => (i === from ? sortHand([...without(h, [card]), back], trump) : i === to ? sortHand([...without(h, [back]), card], trump) : h));
  return { ...s, hands };
}

/** One exchange done: the pair's count goes down; the exchange's end opens the musik. */
function swapDone(s: State358, done: SwapDone): State358 {
  const [first, ...rest] = s.swaps;
  const swaps = first!.left > 1 ? [{ ...first!, left: first!.left - 1 }, ...rest] : rest;
  return { ...s, swaps, swapLog: [...s.swapLog, done], pendingTrump: null, phase: swaps.length ? 'exchange' : 'musik' };
}

/** The giver of the first pair gives `card`; the taker gives back its highest card of the suit (the same card when it
 *  has none of it). A trump waits for the taker's own choice (`giveBack`). */
export function giveCard(s: State358, seat: number, card: HCard): State358 | null {
  const right = s.swaps[0];
  if (s.phase !== 'exchange' || s.pendingTrump || !right || right.giver !== seat || !has(s.hands[seat]!, card)) return null;
  if (card.s === trumpOf(s.contract)) return { ...s, pendingTrump: { giver: seat, taker: right.taker, card } };
  const suit = s.hands[right.taker]!.filter((c) => c.s === card.s);
  const back = suit.length ? highestOf(suit) : card;
  const traded = sameCard(back, card) ? s : trade(s, seat, right.taker, card, back);
  return swapDone(traded, { giver: seat, taker: right.taker, gave: card, got: back });
}

/** The taker of a trump gives back any card of its hand (the trump itself too). */
export function giveBack(s: State358, seat: number, card: HCard): State358 | null {
  const p = s.pendingTrump;
  if (s.phase !== 'exchange' || !p || p.taker !== seat) return null;
  if (!has(s.hands[seat]!, card) && !sameCard(card, p.card)) return null;
  const traded = sameCard(card, p.card) ? s : trade(s, p.giver, seat, p.card, card);
  return swapDone(traded, { giver: p.giver, taker: seat, gave: p.card, got: card });
}

/** The giver of the first pair gives no more cards (the exchange is a right, not a duty). */
export function stopGiving(s: State358, seat: number): State358 | null {
  if (s.phase !== 'exchange' || s.pendingTrump || s.swaps[0]?.giver !== seat) return null;
  const swaps = s.swaps.filter((r) => r.giver !== seat);
  return { ...s, swaps, phase: swaps.length ? 'exchange' : 'musik' };
}

/** The declarer takes the musik (20 cards in the hand until 4 are laid away). */
export function takeMusik(s: State358): State358 {
  if (s.phase !== 'musik' || !s.musik.length) return s;
  const d = s.declarer;
  return { ...s, musik: [], hands: s.hands.map((h, i) => (i === d ? sortHand([...h, ...s.musik], trumpOf(s.contract)) : h)) };
}

/** The declarer lays 4 cards away; the play begins, the declarer leading. */
export function discard(s: State358, seat: number, cards: readonly HCard[]): State358 | null {
  if (s.phase !== 'musik' || s.musik.length || seat !== s.declarer || cards.length !== 4) return null;
  if (!cards.every((c) => has(s.hands[seat]!, c)) || new Set(cards.map(cardKey)).size !== 4) return null;
  const hands = s.hands.map((h, i) => (i === seat ? without(h, cards) : h));
  return { ...s, hands, discards: cards.slice(), phase: 'play', turn: s.declarer };
}

/** The cards `seat` may play now (empty when it is not its turn). */
export function legalCards(s: State358, seat: number): HCard[] {
  if (s.phase !== 'play' || s.turn !== seat) return [];
  const hand = s.hands[seat]!;
  if (!s.trick.length) return hand.slice();
  const led = s.trick[0]!.card.s;
  const follow = hand.filter((c) => c.s === led);
  if (follow.length) return follow;
  const trump = trumpOf(s.contract);
  const trumps = trump ? hand.filter((c) => c.s === trump) : [];
  return trumps.length ? trumps : hand.slice();
}

/** The seat whose card takes the trick: the highest trump, else the highest of the suit led. */
export function trickWinner(trick: readonly Played[], contract: Contract | null): number {
  const trump = trumpOf(contract);
  const led = trick[0]!.card.s;
  const lead = trump && trick.some((p) => p.card.s === trump) ? trump : led;
  let best: Played | null = null;
  for (const p of trick) if (p.card.s === lead && (!best || p.card.r > best.card.r)) best = p;
  return best!.seat;
}

/** `seat` plays `card`; null when it may not. The third card fills the trick (phase trick-done, `winner` set). */
export function playCard(s: State358, seat: number, card: HCard): State358 | null {
  if (!legalCards(s, seat).some((c) => sameCard(c, card))) return null;
  const hands = s.hands.map((h, i) => (i === seat ? without(h, [card]) : h));
  const trick = [...s.trick, { seat, card }];
  if (trick.length < SEATS) return { ...s, hands, trick, turn: (seat + 1) % SEATS };
  return { ...s, hands, trick, turn: -1, winner: trickWinner(trick, s.contract), phase: 'trick-done' };
}

/** The full trick goes to its winner, who leads next; after the 16th trick the deal is scored. */
export function collectTrick(s: State358): State358 {
  if (s.phase !== 'trick-done') return s;
  const w = s.winner;
  const tricks = s.tricks.map((n, i) => (i === w ? n + 1 : n));
  const next: State358 = { ...s, tricks, gone: [...s.gone, ...s.trick.map((p) => p.card)], trick: [], winner: -1, turn: w, phase: 'play' };
  return next.hands.every((h) => h.length === 0) ? scoreHand(next) : next;
}

function scoreHand(s: State358): State358 {
  const points = dealPoints(s, s.tricks);
  const result: DealResult = { contract: s.contract!, declarer: s.declarer, dealer: s.dealer, tricks: s.tricks, targets: targets(s), points };
  const scores = s.scores.map((v, i) => v + points[i]!);
  return { ...s, turn: -1, scores, history: [...s.history, result], phase: s.handNo + 1 >= s.length ? 'over' : 'hand-end' };
}

/** The seats with the highest total (more than one on a tie). */
export const leaders = (scores: readonly number[]): number[] => {
  const high = Math.max(...scores);
  return scores.flatMap((v, i) => (v === high ? [i] : []));
};
/** The player won: the game is over and no total is above the player's (a tie counts, as in Kierki). */
export const playerWon = (s: State358): boolean => s.phase === 'over' && leaders(s.scores).includes(0);
/** The score of a won game for the Ranking: the player's total x 10 (never below 0). */
export const winScore = (s: State358): number => Math.max(0, s.scores[0]!) * 10;

// ---- the computer players (simple and fair: they see their own hand, the table and the cards gone) ----------------

/** How good `c` would be as the game, judged on `cards` (the declarer's first 6). */
function contractValue(cards: readonly HCard[], c: Contract): number {
  const honours = (x: HCard) => (x.r === 14 ? 3 : x.r === 13 ? 2 : x.r === 12 ? 1 : 0);
  if (c === 'NL') return cards.reduce((v, x) => v + (x.r <= 7 ? 2.2 : x.r <= 9 ? 0.8 : -1.5 * (x.r - 9)), 0) + 1;
  if (c === 'NT') return cards.reduce((v, x) => v + honours(x) * 1.25, 0) - 1.5;
  const suit = cards.filter((x) => x.s === c);
  return suit.length * 2.2 + suit.reduce((v, x) => v + honours(x) * 0.6, 0) + cards.filter((x) => x.s !== c && x.r === 14).length * 1.5;
}

/** The game a computer declarer names, on its first 6 cards. */
export function botDeclare(s: State358, seat: number): Contract {
  const free = available(s, seat);
  // in a full game every seat must play every game: the last ones left are taken as they come
  return free.reduce((best, c) => (contractValue(s.firstSix, c) > contractValue(s.firstSix, best) ? c : best));
}

/** The card a computer player in plus gives: its lowest card of a suit outside the trumps it holds no ace of (it
 *  hopes for the taker's ace or king); null: nothing worth giving. */
export function botGive(s: State358, seat: number): HCard | null {
  const trump = trumpOf(s.contract);
  const hand = s.hands[seat]!;
  const plain = hand.filter((c) => c.s !== trump && c.r < 13);
  const noAce = plain.filter((c) => !hand.some((x) => x.s === c.s && x.r === 14));
  const pool = noAce.length ? noAce : plain;
  return pool.length ? lowestOf(pool) : null;
}

/** The card a computer player gives back for a trump: its lowest card outside the trumps. */
export function botGiveBack(s: State358, seat: number): HCard {
  const trump = trumpOf(s.contract);
  const hand = s.hands[seat]!;
  const plain = hand.filter((c) => c.s !== trump);
  return lowestOf(plain.length ? plain : [...hand, s.pendingTrump!.card]);
}

/** The 4 cards a computer declarer lays away: in "bez lew" its highest, else its lowest outside the trumps, from its
 *  shortest suits first (an empty suit lets a trump take). */
export function botDiscard(s: State358, seat: number): HCard[] {
  const hand = s.hands[seat]!;
  const trump = trumpOf(s.contract);
  const len = (suit: HSuit) => hand.filter((c) => c.s === suit).length;
  const cost = isLowGame(s.contract)
    ? (c: HCard) => -c.r * 10 + len(c.s)
    : (c: HCard) => (c.s === trump ? 1000 : 0) + (c.r >= 13 ? 200 : 0) + c.r * 3 + len(c.s) * 4;
  return hand.slice().sort((a, b) => cost(a) - cost(b)).slice(0, 4);
}

/** The highest card of `suit` still out (not gone, not in `own`), or 0 when none. */
function topOut(s: State358, own: readonly HCard[], suit: HSuit): number {
  for (let r = 14; r >= 2; r--) {
    const c = { s: suit, r };
    if (!has(s.gone, c) && !has(own, c) && !has(s.trick.map((p) => p.card), c)) return r;
  }
  return 0;
}

/** The card a computer player plays (always one of `legalCards`). In a trick game it takes what it can as cheaply as it
 *  can; in "bez lew" it keeps under the cards played and sheds its high cards. */
export function botPlay(s: State358, seat: number): HCard {
  const legal = legalCards(s, seat);
  if (!legal.length) throw new Error(`botPlay: seat ${seat} has no card to play`);
  const hand = s.hands[seat]!;
  const trump = trumpOf(s.contract);
  const last = s.trick.length === SEATS - 1;
  if (isLowGame(s.contract)) {
    if (!s.trick.length) return lowestOf(legal);
    const led = s.trick[0]!.card.s;
    if (legal[0]!.s !== led) return highestOf(legal);
    const high = highestOf(s.trick.filter((p) => p.card.s === led).map((p) => p.card)).r;
    const under = legal.filter((c) => c.r < high);
    if (under.length) return highestOf(under);
    return last ? highestOf(legal) : lowestOf(legal);
  }
  if (!s.trick.length) {
    // a card no one can beat in its suit (outside the trumps, while a trump may still beat it, only if few are out)
    const boss = legal.filter((c) => c.r > topOut(s, hand, c.s) && (c.s === trump || !trump || topOut(s, hand, trump) === 0 || c.r === 14));
    if (boss.length) return highestOf(boss);
    const plain = legal.filter((c) => c.s !== trump);
    if (!plain.length) return lowestOf(legal);
    const len = (suit: HSuit) => hand.filter((c) => c.s === suit).length;
    return plain.reduce((m, c) => (len(c.s) > len(m.s) || (len(c.s) === len(m.s) && c.r < m.r) ? c : m));
  }
  const best = s.trick.find((p) => p.seat === trickWinner(s.trick, s.contract))!.card;
  const beats = (c: HCard) => (c.s === best.s ? c.r > best.r : c.s === trump);
  const winners = legal.filter(beats);
  if (winners.length) {
    if (last) return lowestOf(winners.filter((c) => c.s === winners[0]!.s));
    // second to play: a sure winner if it has one, else its lowest winner when it is high enough to stand
    const sure = winners.filter((c) => c.s === trump || c.r > topOut(s, hand, c.s));
    if (sure.length) return lowestOf(sure);
    const strong = winners.filter((c) => c.r >= 12);
    if (strong.length) return lowestOf(strong);
  }
  // no trick to take: the cheapest card, from a short plain suit
  const plain = legal.filter((c) => c.s !== trump);
  const pool = plain.length ? plain : legal;
  return lowestOf(pool);
}
